import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Paintbrush, Coins, Scale, Users, Cable, Loader2, Plus, Trash2, Copy, ExternalLink, ShieldCheck, ShieldHalf, ShieldAlert, Flag, Eye, EyeOff, Wifi, WifiOff, CheckCircle2, Circle, LayoutGrid, Package, Landmark, Stamp, Radio, Activity, Megaphone } from "lucide-react";
import PortalNav from "@/components/PortalNav";
import api, { formatApiErrorDetail } from "@/lib/api";
import { LoadingStudio } from "@/components/hub/LoadingStudio";
import { StaffPanel } from "@/components/hub/StaffPanel";
import { ReportsPanel } from "@/components/hub/ReportsPanel";
import { BlueprintPanel } from "@/components/hub/BlueprintPanel";
import { PoliticsPanel } from "@/components/hub/PoliticsPanel";
import { BanIntelPanel } from "@/components/hub/BanIntelPanel";
import { DigestPanel } from "@/components/hub/DigestPanel";
import { BulletinsPanel } from "@/components/hub/BulletinsPanel";
import BridgeWizard from "@/components/hub/BridgeWizard";

const inputCls = "w-full bg-[#0A0A0C] border border-white/10 px-3 py-2.5 font-mono text-xs text-zinc-100 focus:outline-none focus:ring-1 focus:ring-nc-orange focus:border-nc-orange transition-all";
const labelCls = "font-mono text-[9px] font-bold tracking-[0.25em] text-zinc-500 uppercase block mb-1.5";
const card = "border border-white/[0.08] bg-[#0A0A0C] p-6 flex flex-col gap-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.03)]";
const FACTION_TYPES = ["police", "ems", "fire", "criminal", "business", "custom"];
const RULESET_FLAGS = [
  ["perma_death", "Perma-death", "Dead characters stay dead — Next of Kin will applies"],
  ["realistic_injuries", "Realistic injuries", "Wounds persist and need medical treatment"],
  ["voice_rp_only", "Voice RP only", "No text RP channels in character"],
  ["hardcore_mode", "Hardcore mode", "Harsher economy, limited HUD aids"],
  ["whitelisted_jobs", "Whitelisted jobs", "Police/EMS/fire require approved applications"],
  ["whitelist_factions", "Whitelisted factions", "Sheriff & Fire Rescue require a staff whitelist at the spawn select (official servers keep this OFF)"],
  ["mayor_persistent", "Persistent mayor", "Mayor keeps office through death — only removal ends the term"],
  ["night_cycle_realtime", "Real-time night cycle", "In-game clock follows real time"],
];

export default function OwnerDeckPage() {
  const [params] = useSearchParams();
  const [me, setMe] = useState(null);
  const [servers, setServers] = useState([]);
  const [sid, setSid] = useState(params.get("server") || "");
  const [deck, setDeck] = useState(null);
  const [tab, setTab] = useState("branding");
  const [busy, setBusy] = useState(false);
  const [keyShown, setKeyShown] = useState(false);
  const [theme, setTheme] = useState({ accent: "#2E6BFF", video_url: "", logo_url: "", rulesText: "", audioText: "" });
  const [econ, setEcon] = useState(null);
  const [ruleset, setRuleset] = useState(null);
  const [faction, setFaction] = useState({ name: "", type: "custom", pay_per_tick: 100, color: "#2E6BFF", whitelist_only: false, max_slots: 0 });
  const [members, setMembers] = useState({});
  const [openFaction, setOpenFaction] = useState("");
  const [memberForm, setMemberForm] = useState({ playercode: "", name: "", rank: 1 });
  const [widgets, setWidgets] = useState([]);
  const [gate, setGate] = useState({ enabled: false, pass_min: 70, require_steam: false, questions: [] });
  const [intro, setIntro] = useState({ logo_url: "", tagline: "", discord: "", rulesText: "", cameraText: "" });

  useEffect(() => {
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => setMe(null));
  }, []);

  useEffect(() => {
    if (!me) return;
    if (me.role === "admin") {
      api.get("/admin/ncdb/servers").then((r) => setServers(r.data.map((s) => ({ id: s.id, name: s.name })))).catch(() => {});
    } else {
      api.get("/nc/servers").then((r) => {
        setServers(r.data.servers.filter((s) => s.verified && s.owner_id === me.id).map((s) => ({ id: s.id, name: s.name })));
      }).catch(() => {});
    }
  }, [me]);

  const load = useCallback(() => {
    if (!sid) return;
    api.get(`/nc/servers/${sid}/deck`).then((r) => {
      setDeck(r.data);
      const t = r.data.theme || {};
      setTheme({ accent: t.accent || "#2E6BFF", video_url: t.video_url || "", logo_url: t.logo_url || "", rulesText: (t.rules || []).join("\n"), audioText: (t.audio || []).join("\n") });
      setWidgets(t.widgets || []);
      setEcon(r.data.economy);
      setRuleset(r.data.ruleset);
      api.get(`/nc/servers/${sid}/gate`).then((g) => setGate(g.data.gate || { enabled: false, pass_min: 70, require_steam: false, questions: [] })).catch(() => {});
      api.get(`/nc/servers/${sid}/intro`).then((r) => {
        const i = r.data.intro || {};
        setIntro({ logo_url: i.logo_url || "", tagline: i.tagline || "", discord: i.discord || "",
                   rulesText: (i.rules || []).join("\n"), cameraText: (i.camera || []).join("\n") });
      }).catch(() => {});
    }).catch((e) => {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setDeck(null);
    });
  }, [sid]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (!sid && servers.length) setSid(servers[0].id); }, [servers, sid]);

  const saveTheme = async () => {
    setBusy(true);
    try {
      const r = await api.patch(`/nc/servers/${sid}/theme`, {
        accent: theme.accent, video_url: theme.video_url, logo_url: theme.logo_url,
        rules: theme.rulesText.split("\n").map((x) => x.trim()).filter(Boolean),
        audio: theme.audioText.split("\n").map((x) => x.trim()).filter(Boolean),
        widgets,
      });
      toast.success(r.data.message);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const saveWidgets = async () => {
    setBusy(true);
    try {
      await api.patch(`/nc/servers/${sid}/theme`, { widgets });
      toast.success("Widgets published — players see them on their next join");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const saveGate = async () => {
    setBusy(true);
    try {
      const r = await api.patch(`/nc/servers/${sid}/gate`, gate);
      toast.success(r.data.message);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const saveEconomy = async () => {
    setBusy(true);
    try {
      const r = await api.patch(`/nc/servers/${sid}/economy`, { ...econ, starting_cash: +econ.starting_cash, paycheck: +econ.paycheck, tax_income: +econ.tax_income, tax_sales: +econ.tax_sales, tax_vehicle: +econ.tax_vehicle });
      toast.success(r.data.message);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const saveRuleset = async (next) => {
    setRuleset(next);
    try {
      const r = await api.patch(`/nc/servers/${sid}/ruleset`, next);
      toast.success(r.data.message);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const addFaction = async () => {
    setBusy(true);
    try {
      await api.post(`/nc/servers/${sid}/factions`, { ...faction, pay_per_tick: +faction.pay_per_tick, max_slots: +faction.max_slots });
      toast.success("Faction created");
      setFaction({ name: "", type: "custom", pay_per_tick: 100, color: "#2E6BFF", whitelist_only: false, max_slots: 0 });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const removeFaction = async (fid) => {
    await api.delete(`/nc/servers/${sid}/factions/${fid}`);
    toast.success("Faction removed");
    load();
  };

  const loadMembers = async (fid) => {
    try {
      const r = await api.get(`/nc/servers/${sid}/factions/${fid}/members`);
      setMembers((m) => ({ ...m, [fid]: r.data }));
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const toggleFaction = (fid) => {
    const next = openFaction === fid ? "" : fid;
    setOpenFaction(next);
    if (next) loadMembers(next);
  };

  const saveMember = async (fid, playercode, name, rank) => {
    setBusy(true);
    try {
      const r = await api.post(`/nc/servers/${sid}/factions/${fid}/members`, { playercode: Number(playercode), name, rank: Number(rank) });
      toast.success(r.data.message);
      setMemberForm({ playercode: "", name: "", rank: 1 });
      loadMembers(fid);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const kickMember = async (fid, playercode) => {
    await api.delete(`/nc/servers/${sid}/factions/${fid}/members/${playercode}`);
    toast.success("Member removed");
    loadMembers(fid);
  };

  const copy = (text, label) => { navigator.clipboard?.writeText(text).then(() => toast.success(`${label} copied`)).catch(() => {}); };

  const saveIntro = async () => {
    setBusy(true);
    try {
      const r = await api.patch(`/nc/servers/${sid}/intro`, {
        logo_url: intro.logo_url, tagline: intro.tagline, discord: intro.discord,
        rules: intro.rulesText.split("\n").map((s) => s.trim()).filter(Boolean),
        camera: intro.cameraText.split("\n").map((s) => s.trim()).filter(Boolean),
      });
      toast.success(r.data.message);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  if (me === null) return <div className="min-h-screen bg-[#050505] text-zinc-100 p-6"><PortalNav title="Server Deck" accent="text-nc-orange" /><p className="font-mono text-xs text-zinc-500 mt-10 text-center">Sign in to manage your server.</p></div>;

  return (
    <div data-testid="owner-deck" className="min-h-screen bg-[#050505] text-zinc-100 p-4 sm:p-6 flex flex-col gap-5">
      <PortalNav title="Server Deck" accent="text-nc-orange" />

      {/* ── Command telemetry header ── */}
      {deck && (() => {
        const live = deck.snapshot?.at && Date.now() - new Date(deck.snapshot.at).getTime() < 300000;
        const ageSec = deck.snapshot?.at ? Math.max(0, Math.round((Date.now() - new Date(deck.snapshot.at).getTime()) / 1000)) : null;
        return (
          <div className="sticky top-0 z-40 -mx-4 sm:-mx-6 px-4 sm:px-6 py-3 bg-[#050505]/92 backdrop-blur-md border-b border-white/[0.08]">
            <div className="flex items-center gap-4 flex-wrap">
              {/* brand badge + switcher */}
              <div className="flex items-center gap-3">
                <span className="w-9 h-9 flex items-center justify-center border border-nc-orange/50 bg-nc-orange/10 shadow-[0_0_18px_rgba(255,107,26,0.15)]">
                  <ShieldCheck className="w-4 h-4 text-nc-orange" />
                </span>
                <div>
                  <h1 className="font-sans font-bold uppercase tracking-widest text-sm text-zinc-100 leading-tight">Owner Deck</h1>
                  <p className="font-mono text-[8px] uppercase tracking-[0.25em] text-zinc-600 leading-tight">command console</p>
                </div>
                <select data-testid="deck-server-select" value={sid} onChange={(e) => setSid(e.target.value)}
                  className="ml-2 bg-[#0A0A0C] border border-white/10 hover:border-nc-orange/40 text-zinc-100 font-mono text-[10px] uppercase px-3 py-2.5 focus:outline-none focus:border-nc-orange transition-colors">
                  {servers.length === 0 && <option value="">No verified servers yet</option>}
                  {servers.map((s) => <option key={s.id} value={s.id}>{s.name}{s.address ? ` — ${s.address}` : " — sandbox"}</option>)}
                </select>
              </div>
              {/* live telemetry */}
              <div data-testid="deck-telemetry" className="flex items-center gap-2 border border-white/[0.08] bg-[#0A0A0C] px-3 py-2">
                <span className={`relative flex w-2 h-2`}>
                  {live && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00E676] opacity-60" />}
                  <span className={`relative inline-flex rounded-full w-2 h-2 ${live ? "bg-[#00E676]" : "bg-zinc-600"}`} />
                </span>
                <span className={`font-mono text-[9px] uppercase tracking-[0.15em] ${live ? "text-[#00E676]" : "text-zinc-500"}`}>
                  {live ? `${deck.snapshot.players}/${deck.snapshot.max_players} online` : "offline"}
                </span>
                {ageSec != null && <span className="font-mono text-[8px] text-zinc-600 uppercase">hb {ageSec}s ago</span>}
              </div>
              {/* quick actions */}
              <div className="ml-auto flex items-center gap-2">
                <a data-testid="deck-join-preview" href={`/cef/join/${sid}`} target="_blank" rel="noopener noreferrer"
                  className="font-mono text-[9px] uppercase tracking-[0.15em] text-zinc-400 border border-white/10 px-3 py-2 hover:border-white/40 hover:text-zinc-100 transition-colors flex items-center gap-1.5">
                  Join Flow <ExternalLink className="w-3 h-3" /></a>
                <a data-testid="deck-loading-preview" href={`/cef/loading/${sid}`} target="_blank" rel="noopener noreferrer"
                  className="font-mono text-[9px] uppercase tracking-[0.15em] text-nc-orange border border-nc-orange/40 px-3 py-2 hover:bg-nc-orange/10 hover:shadow-[0_0_15px_rgba(255,107,26,0.2)] transition-all flex items-center gap-1.5">
                  Loading Screen <ExternalLink className="w-3 h-3" /></a>
              </div>
            </div>
          </div>
        );
      })()}

      {servers.length === 0 && (
        <div className="border border-dashed border-white/15 p-10 text-center">
          <p className="font-mono text-xs text-zinc-500 leading-relaxed">No verified servers on your account. Register and claim one from the <a href="/new-citizen/servers" className="text-nc-blue underline underline-offset-4">server browser</a> first.</p>
        </div>
      )}

      {deck && (
        <>
          {/* ── 4-pillar navigation dock ── */}
          <div className="border border-white/[0.08] bg-[#0A0A0C] px-3 py-2.5 flex flex-wrap items-center gap-x-7 gap-y-2.5">
            {[
              ["Identity & Gateway", [["branding", Paintbrush, "Identity & Loading"], ["intro", Stamp, "Passport Intro"], ["studio", LayoutGrid, "Loading Studio"]]],
              ["Operations & Moderation", [["staff", ShieldHalf, "Staff & Roles"], ["reports", Flag, "Reports"], ["airwaves", Radio, "Airwaves"], ...(me?.role === "admin" ? [["banintel", ShieldAlert, "Ban Intel"], ["digest", Activity, "Digest"], ["bulletins", Megaphone, "Bulletins"]] : [])]],
              ["World & Economy", [["blueprint", Package, "Blueprints"], ["politics", Landmark, "Politics"], ["economy", Coins, "Economy"], ["ruleset", Scale, "Ruleset"], ["factions", Users, `Factions (${deck.factions.length})`]]],
              ["Infrastructure", [["connect", Cable, "Connect Game"]]],
            ].map(([pillar, items]) => (
              <div key={pillar} className="flex items-center gap-1.5 flex-wrap">
                <span className="font-mono text-[7px] uppercase tracking-[0.3em] text-zinc-600 mr-1 select-none">{pillar}</span>
                {items.map(([id, Icon, label]) => (
                  <button key={id} data-testid={`deck-tab-${id}`} onClick={() => setTab(id)}
                    className={`relative font-mono text-[10px] uppercase tracking-[0.15em] px-3 py-2 transition-colors duration-150 flex items-center gap-1.5 ${tab === id ? "text-nc-orange" : "text-zinc-500 hover:text-zinc-200"}`}>
                    {tab === id && (
                      <motion.span layoutId="active-tab-glow" className="absolute inset-0 bg-nc-orange/10 border border-nc-orange/50 shadow-[0_0_15px_rgba(255,107,26,0.15)]"
                        transition={{ type: "spring", bounce: 0.18, duration: 0.45 }} />
                    )}
                    <Icon className="w-3 h-3 relative" /> <span className="relative">{label}</span>
                  </button>
                ))}
              </div>
            ))}
          </div>

          {/* ── animated command canvas ── */}
          <AnimatePresence mode="wait">
            <motion.div key={tab} initial={{ opacity: 0, y: 10, scale: 0.99 }} animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2, ease: "easeOut" }}>

          {tab === "branding" && (
            <div className={card}>
              <p className="font-mono text-[10px] tracking-[0.3em] text-nc-orange uppercase">Identity & Loading Screen</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <span className={labelCls}>HUD accent color</span>
                  <div className="flex gap-2">
                    <input data-testid="theme-accent" className={inputCls} value={theme.accent} maxLength={7} onChange={(e) => setTheme({ ...theme, accent: e.target.value })} />
                    <span className="w-10 border border-white/15 shrink-0" style={{ backgroundColor: /^#[0-9a-fA-F]{6}$/.test(theme.accent) ? theme.accent : "transparent" }} />
                  </div>
                </div>
                <div><span className={labelCls}>Backdrop video (https .mp4/.webm)</span><input data-testid="theme-video" className={inputCls} placeholder="https://cdn.example.com/loop.mp4" value={theme.video_url} onChange={(e) => setTheme({ ...theme, video_url: e.target.value })} /></div>
                <div><span className={labelCls}>Logo URL (https)</span><input data-testid="theme-logo" className={inputCls} placeholder="https://…" value={theme.logo_url} onChange={(e) => setTheme({ ...theme, logo_url: e.target.value })} /></div>
                <div className="sm:col-span-2"><span className={labelCls}>Audio playlist — one https mp3/ogg URL per line; first track plays on the loading screen</span><textarea data-testid="theme-audio" rows={2} className={`${inputCls} resize-none`} placeholder="https://cdn.example.com/theme.mp3" value={theme.audioText} onChange={(e) => setTheme({ ...theme, audioText: e.target.value })} /></div>
                <div className="sm:col-span-2"><span className={labelCls}>Rule cards — one per line, max 10</span><textarea data-testid="theme-rules" rows={5} className={`${inputCls} resize-none`} value={theme.rulesText} onChange={(e) => setTheme({ ...theme, rulesText: e.target.value })} /></div>
              </div>
              <button data-testid="theme-save" disabled={busy} onClick={saveTheme} className="bg-nc-orange hover:bg-nc-orange/80 disabled:opacity-40 text-black font-mono text-[10px] tracking-[0.2em] uppercase px-5 py-3 transition-colors w-fit flex items-center gap-2">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paintbrush className="w-4 h-4" />} Apply Identity
              </button>
              <p className="font-mono text-[9px] text-zinc-400">Players see the new identity on their next join — no server restart needed.</p>
            </div>
          )}

          {tab === "intro" && (
            <div className={card}>
              <p className="font-mono text-[10px] tracking-[0.3em] text-nc-orange uppercase">Passport Control — Join Intro</p>
              <p className="font-mono text-[9px] text-zinc-400 leading-relaxed -mt-2">
                What new arrivals see before registration: your branding, a tagline, and the house rules they must accept
                before their passport is issued. Camera cues are read by the engine for the intro pans.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><span className={labelCls}>Intro logo (https or hub media upload)</span><input data-testid="intro-logo" className={inputCls} placeholder="https://… or /api/nc/media/…" value={intro.logo_url} onChange={(e) => setIntro({ ...intro, logo_url: e.target.value })} /></div>
                <div><span className={labelCls}>Community Discord link</span><input data-testid="intro-discord" className={inputCls} placeholder="https://discord.gg/…" value={intro.discord} onChange={(e) => setIntro({ ...intro, discord: e.target.value })} /></div>
                <div className="sm:col-span-2"><span className={labelCls}>Tagline — one line, shown in italics at passport control</span><input data-testid="intro-tagline" className={inputCls} maxLength={120} placeholder="Serious county RP. Every shift tells a story." value={intro.tagline} onChange={(e) => setIntro({ ...intro, tagline: e.target.value })} /></div>
                <div className="sm:col-span-2"><span className={labelCls}>House rules — one per line, max 10; players must tick agree before registering</span><textarea data-testid="intro-rules" rows={5} className={`${inputCls} resize-none`} placeholder={"Stay in character on the streets.\nNo random deathmatch."} value={intro.rulesText} onChange={(e) => setIntro({ ...intro, rulesText: e.target.value })} /></div>
                <div className="sm:col-span-2"><span className={labelCls}>Camera pans — one cue per line, max 5 (engine reads these during the intro)</span><textarea data-testid="intro-camera" rows={3} className={`${inputCls} resize-none`} placeholder={"pan-harbor-sunrise\ndolly-founders-square"} value={intro.cameraText} onChange={(e) => setIntro({ ...intro, cameraText: e.target.value })} /></div>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <button data-testid="intro-save" disabled={busy} onClick={saveIntro} className="bg-nc-orange hover:bg-nc-orange/80 disabled:opacity-40 text-black font-mono text-[10px] tracking-[0.2em] uppercase px-5 py-3 transition-colors w-fit flex items-center gap-2">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Stamp className="w-4 h-4" />} Save Passport Intro
                </button>
                <a data-testid="intro-preview" href={`/cef/join/${sid}`} target="_blank" rel="noopener noreferrer"
                  className="font-mono text-[9px] uppercase text-nc-blue border border-nc-blue/40 px-3 py-2.5 hover:bg-nc-blue/10 transition-colors flex items-center gap-1.5">
                  Preview join flow <ExternalLink className="w-3 h-3" />
                </a>
              </div>
              <p className="font-mono text-[9px] text-zinc-400">Applies instantly — no restart. Leave rules empty to skip the agreement step.</p>
            </div>
          )}

          {tab === "studio" && (
            <LoadingStudio sid={sid} widgets={widgets} setWidgets={setWidgets} gate={gate} setGate={setGate}
              onSaveWidgets={saveWidgets} onSaveGate={saveGate} busy={busy} onChanged={load} />
          )}

          {tab === "staff" && <StaffPanel sid={sid} />}

          {tab === "reports" && <ReportsPanel sid={sid} isAdmin={me.role === "admin"} />}

          {tab === "banintel" && me.role === "admin" && <BanIntelPanel />}

          {tab === "digest" && me.role === "admin" && <DigestPanel />}

          {tab === "bulletins" && me.role === "admin" && <BulletinsPanel sid={sid} />}

          {tab === "blueprint" && <BlueprintPanel sid={sid} me={me} onApplied={load} />}

          {tab === "politics" && <PoliticsPanel sid={sid} />}

          {tab === "economy" && econ && (
            <div className={card}>
              <p className="font-mono text-[10px] tracking-[0.3em] text-nc-orange uppercase">Economy Balances</p>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                {[["starting_cash", "Starting cash $"], ["paycheck", "Paycheck $"], ["tax_income", "Income tax %"], ["tax_sales", "Sales tax %"], ["tax_vehicle", "Vehicle fee %"]].map(([k, label]) => (
                  <div key={k}><span className={labelCls}>{label}</span><input data-testid={`econ-${k}`} type="number" className={inputCls} value={econ[k]} onChange={(e) => setEcon({ ...econ, [k]: e.target.value })} /></div>
                ))}
              </div>
              <button data-testid="econ-save" disabled={busy} onClick={saveEconomy} className="bg-nc-orange hover:bg-nc-orange/80 disabled:opacity-40 text-black font-mono text-[10px] tracking-[0.2em] uppercase px-5 py-3 transition-colors w-fit flex items-center gap-2">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Coins className="w-4 h-4" />} Save Economy
              </button>
              <p className="font-mono text-[9px] text-zinc-400">The bridge polls /config and applies these live — paycheck ticks and shop prices update without a restart.</p>
            </div>
          )}

          {tab === "ruleset" && ruleset && (
            <div className={card}>
              <p className="font-mono text-[10px] tracking-[0.3em] text-nc-orange uppercase">Roleplay Ruleset Presets</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {RULESET_FLAGS.map(([key, label, desc]) => (
                  <button key={key} data-testid={`ruleset-${key}`} onClick={() => saveRuleset({ ...ruleset, [key]: !ruleset[key] })}
                    className={`text-left border p-4 transition-colors ${ruleset[key] ? "border-nc-orange/60 bg-nc-orange/5" : "border-white/10 hover:border-white/30"}`}>
                    <p className={`font-mono text-[10px] uppercase tracking-[0.2em] flex items-center justify-between ${ruleset[key] ? "text-nc-orange" : "text-zinc-300"}`}>{label} <span>{ruleset[key] ? "ON" : "OFF"}</span></p>
                    <p className="font-mono text-[9px] text-zinc-500 mt-1.5">{desc}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {tab === "factions" && (
            <div className={card}>
              <p className="font-mono text-[10px] tracking-[0.3em] text-nc-orange uppercase">Factions & Jobs</p>
              {deck.factions.length === 0 && <p className="font-mono text-[10px] text-zinc-400">No custom factions yet — create your police department, EMS, businesses…</p>}
              {deck.factions.map((f) => (
                <div key={f.id} data-testid={`faction-row-${f.id.slice(0, 8)}`} className="border border-white/10">
                  <div className="px-4 py-3 flex items-center gap-3 flex-wrap">
                    <span className="w-3 h-3 shrink-0" style={{ backgroundColor: f.color }} />
                    <span className="font-sans font-semibold text-sm">{f.name}</span>
                    <span className="font-mono text-[8px] uppercase border border-white/15 text-zinc-400 px-1.5 py-0.5">{f.type}</span>
                    <span className="font-mono text-[9px] text-zinc-500">${f.pay_per_tick}/tick</span>
                    {f.whitelist_only && <span className="font-mono text-[8px] uppercase text-nc-blue border border-nc-blue/40 px-1.5 py-0.5">whitelist</span>}
                    {f.max_slots > 0 && <span className="font-mono text-[8px] text-zinc-500">{f.max_slots} slots</span>}
                    <button data-testid={`faction-members-${f.id.slice(0, 8)}`} onClick={() => toggleFaction(f.id)} className="ml-auto font-mono text-[8px] uppercase border border-white/15 text-zinc-400 hover:border-nc-orange hover:text-nc-orange px-2 py-1.5 transition-colors flex items-center gap-1"><Users className="w-3 h-3" /> Roster</button>
                    <button data-testid={`faction-del-${f.id.slice(0, 8)}`} onClick={() => removeFaction(f.id)} className="text-zinc-500 hover:text-red-400 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                  {openFaction === f.id && (
                    <div data-testid="faction-roster" className="border-t border-white/5 px-4 py-3 flex flex-col gap-2">
                      <p className="font-mono text-[8px] uppercase tracking-[0.2em] text-zinc-400">Ranks R1–R8 — promotions update job access on the player's next join instantly</p>
                      {(members[f.id] || []).map((m) => (
                        <div key={m.playercode} data-testid={`member-${m.playercode}`} className="flex items-center gap-3 flex-wrap border-b border-white/5 pb-2">
                          <span className="font-mono text-[10px] text-zinc-200">{m.name} <span className="text-zinc-400">#{m.playercode}</span></span>
                          <select data-testid={`member-rank-${m.playercode}`} value={m.rank} onChange={(e) => saveMember(f.id, m.playercode, m.name, e.target.value)} className="bg-[#050505] border border-white/15 px-2 py-1.5 font-mono text-[10px] text-nc-orange">
                            {(f.ranks || []).map((title, i) => <option key={i + 1} value={i + 1}>R{i + 1} — {title}</option>)}
                          </select>
                          <button onClick={() => kickMember(f.id, m.playercode)} className="ml-auto text-zinc-400 hover:text-red-400 transition-colors"><Trash2 className="w-3 h-3" /></button>
                        </div>
                      ))}
                      {(members[f.id] || []).length === 0 && <p className="font-mono text-[9px] text-zinc-400">No members yet — enlist your first {f.type} below.</p>}
                      <div className="flex items-end gap-2 flex-wrap pt-1">
                        <div><span className={labelCls}>Citizen code</span><input data-testid="member-playercode" inputMode="numeric" className={`${inputCls} w-28`} value={memberForm.playercode} onChange={(e) => setMemberForm({ ...memberForm, playercode: e.target.value.replace(/\D/g, "") })} placeholder="37049" /></div>
                        <div><span className={labelCls}>Name</span><input data-testid="member-name" className={`${inputCls} w-40`} value={memberForm.name} maxLength={80} onChange={(e) => setMemberForm({ ...memberForm, name: e.target.value })} placeholder="Ada King" /></div>
                        <div><span className={labelCls}>Rank</span>
                          <select data-testid="member-rank-new" value={memberForm.rank} onChange={(e) => setMemberForm({ ...memberForm, rank: e.target.value })} className="bg-[#050505] border border-white/10 px-2 py-2.5 font-mono text-[11px] text-zinc-100">
                            {(f.ranks || []).map((title, i) => <option key={i + 1} value={i + 1}>R{i + 1} — {title}</option>)}
                          </select>
                        </div>
                        <button data-testid="member-add" disabled={busy || !memberForm.playercode || memberForm.name.trim().length < 1} onClick={() => saveMember(f.id, memberForm.playercode, memberForm.name, memberForm.rank)} className="font-mono text-[9px] uppercase px-4 py-2.5 bg-nc-orange hover:bg-nc-orange/80 text-black tracking-[0.15em] transition-colors disabled:opacity-40 flex items-center gap-1.5">
                          <Plus className="w-3 h-3" /> Enlist / Promote
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              <div className="border-t border-white/10 pt-4 grid grid-cols-2 sm:grid-cols-6 gap-3 items-end">
                <div className="col-span-2"><span className={labelCls}>Faction name</span><input data-testid="faction-name" className={inputCls} value={faction.name} maxLength={40} onChange={(e) => setFaction({ ...faction, name: e.target.value })} placeholder="BPD" /></div>
                <div><span className={labelCls}>Type</span><select data-testid="faction-type" className={inputCls} value={faction.type} onChange={(e) => setFaction({ ...faction, type: e.target.value })}>{FACTION_TYPES.map((t) => <option key={t}>{t}</option>)}</select></div>
                <div><span className={labelCls}>Pay / tick</span><input data-testid="faction-pay" type="number" className={inputCls} value={faction.pay_per_tick} onChange={(e) => setFaction({ ...faction, pay_per_tick: e.target.value })} /></div>
                <div><span className={labelCls}>Color</span><input data-testid="faction-color" className={inputCls} value={faction.color} maxLength={7} onChange={(e) => setFaction({ ...faction, color: e.target.value })} /></div>
                <label className="flex items-center gap-2 font-mono text-[9px] uppercase text-zinc-400 pb-2.5"><input data-testid="faction-wl" type="checkbox" checked={faction.whitelist_only} onChange={(e) => setFaction({ ...faction, whitelist_only: e.target.checked })} className="accent-nc-orange" /> Whitelist</label>
              </div>
              <button data-testid="faction-add" disabled={busy || faction.name.trim().length < 3} onClick={addFaction} className="bg-nc-orange hover:bg-nc-orange/80 disabled:opacity-40 text-black font-mono text-[10px] tracking-[0.2em] uppercase px-5 py-3 transition-colors w-fit flex items-center gap-2">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add Faction
              </button>
            </div>
          )}

          {tab === "connect" && <BridgeWizard sid={sid} deck={deck} />}
          {tab === "airwaves" && <AirwavesPanel sid={sid} />}

            </motion.div>
          </AnimatePresence>
        </>
      )}
    </div>
  );
}

/* -------- Airwaves — push one faction recruitment transmission to every player's main menu -------- */
function AirwavesPanel({ sid }) {
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const push = async () => {
    if (message.trim().length < 4 || busy) return;
    setBusy(true);
    try {
      const r = await api.post(`/nc/servers/${sid}/menu/broadcast`, { message: message.trim() });
      toast.success(r.data.message);
      setMessage("");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Transmission failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className={card} data-testid="airwaves-panel">
      <p className="font-mono text-[10px] tracking-[0.3em] text-nc-orange uppercase flex items-center gap-2"><Radio className="w-3.5 h-3.5" /> Faction Recruitment Broadcast</p>
      <p className="font-mono text-[9px] text-zinc-400 leading-relaxed -mt-2">
        One transmission appears as a radio overlay on every player's main menu for 48 hours — recruiting for your PD,
        your crew, your business. One active broadcast per server, max 4 per day. The airwaves stay rare on purpose.
      </p>
      <textarea data-testid="broadcast-message" rows={3} maxLength={180} value={message} onChange={(e) => setMessage(e.target.value)}
        placeholder="The Marshals are hiring. Clean record, steady aim. Ask at the desk. — Capt. Rios"
        className={`${inputCls} resize-none`} />
      <div className="flex items-center gap-3">
        <button data-testid="broadcast-send" disabled={busy || message.trim().length < 4} onClick={push}
          className="bg-nc-orange hover:bg-nc-orange/80 disabled:opacity-40 text-black font-mono text-[10px] tracking-[0.2em] uppercase px-5 py-3 transition-colors flex items-center gap-2">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Radio className="w-4 h-4" />} Transmit
        </button>
        <span className="font-mono text-[8px] text-zinc-500 uppercase tracking-[0.15em]">{message.length}/180 · replaces any live broadcast</span>
      </div>
    </div>
  );
}
